import React from 'react';
import { Route } from 'react-router-dom';
import lazyLoad from 'utils/lazyLoad';
import { useSelector } from 'react-redux';
import useFeatureToggle from 'hooks/useFeatureToggle';
import {
  hasViewConferenceBridges,
  hasViewDictionary,
  hasViewMenuDeveloper,
  hasViewRecording,
} from 'selectors/permissions';
import { getRouterPath, getPathsForGwtPlace } from 'utils/navigation';
import {
  isVoiceRecordingListPageEnabled,
  isTtsDictionaryEnabled,
} from 'selectors/featureToggles';

import useWorkflowsListRoutes from './useWorkflowsListRoutes';
import useFloodControlRoutes from './useFloodControlRoutes';
import useXAgentRoutes from './useXAgentRoutes';
import { UnauthorizedPageAccess } from './utils';

const OauthCredentialsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "OauthCredentialsPage" */ 'pages/OauthCredentialsPage'
    ),
);
const ExternalConferenceBridgesListPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "ExternalConferenceBridgesListPage" */ 'pages/ExternalConferenceBridgesListPage'
    ),
);
const IntegrationManager = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IntegrationManager" */ 'pages/IntegrationManager'
    ),
);
const VoiceRecordingListPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "VoiceRecordingListPage" */ 'pages/VoiceRecordingListPage'
    ),
);

const TtsDictionaryPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "TtsDictionaryPage" */ 'pages/TtsDictionaryPage'
    ),
);

export default function useWorkflowsRoutes() {
  const canViewConferenceBridges = useSelector(hasViewConferenceBridges);
  const isReactOAuthTokensPageEnabled = useFeatureToggle(
    'REACT_OAUTH_TOKENS_PAGE',
  );
  const ttsDictionaryEnabled = useSelector(isTtsDictionaryEnabled);
  const canViewWorkflows = useSelector(hasViewMenuDeveloper);
  const canViewDictionary = useSelector(hasViewDictionary);
  const canViewRecording = useSelector(hasViewRecording);

  return [
    ...useWorkflowsListRoutes(),
    <Route
      path={getPathsForGwtPlace('integrationManager')}
      component={canViewWorkflows ? IntegrationManager : UnauthorizedPageAccess}
    />,
    ...useXAgentRoutes(),
    ...useFloodControlRoutes(),
    isReactOAuthTokensPageEnabled && (
      <Route
        path={getRouterPath('/OauthCredentialsPage')}
        component={
          canViewWorkflows ? OauthCredentialsPage : UnauthorizedPageAccess
        }
      />
    ),
    <Route
      path={getRouterPath('/ExternalConferenceBridgeListPage')}
      component={
        canViewConferenceBridges
          ? ExternalConferenceBridgesListPage
          : UnauthorizedPageAccess
      }
    />,
    isVoiceRecordingListPageEnabled && (
      <Route
        path={getRouterPath('/VoiceRecordingListPage')}
        component={
          canViewRecording ? VoiceRecordingListPage : UnauthorizedPageAccess
        }
      />
    ),
    ttsDictionaryEnabled && (
      <Route
        path={getRouterPath('/TtsDictionaryPage')}
        component={
          canViewDictionary ? TtsDictionaryPage : UnauthorizedPageAccess
        }
      />
    ),
  ];
}
